import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import * as es6printJS from "print-js";
import { environment } from "src/environments/environment";
import { Payment } from "../../models/payment";
import { ChargesStoreService } from "../../services/charges-store.service";

export interface RecebimentosReportData {
  periodo: {
    inicio: string;
    fim: string;
  };
  cobradores: CobradorRecebimentos[];
  totalGeral: TotalizadorPagamento[];
  pixNaoPagos: PixNaoPago[];
}

export interface CobradorRecebimentos {
  nome: string;
  cpf: string;
  recebimentos: RecebimentoItem[];
  totalizador: TotalizadorPagamento[];
}

export interface RecebimentoItem {
  paymentId: string;
  contrato: string;
  dataVencimento: string;
  dataPagamento: string;
  nomeCliente: string;
  valorPago: number;
  formasPagamento: string[];
  cnpj: string;
}

export interface TotalizadorPagamento {
  formaPagamento: string;
  valor: number;
}

interface BaixaDistribution {
  formaPagamento: string;
  valorBase: number;
}

export interface PixNaoPago {
  contrato: string;
  dataGeracao: string;
  nomeCliente: string;
  valor: number;
  status: string;
}

export interface PixTransaction {
  cgc_cliente: string;
  cnpj: string;
  contrato: string;
  data_solicitacao_origem: Date;
  txid: string;
  origem?: string;
  chave_titulo?: string[];
  valor: number;
  valorPago?: string;
  situacao: string; // TotalBankStatus
  valorDevolvido?: number;
  pagadorEfetivoInscricao?: string;
  dataDevolucao?: string;
  dataLiquidacao?: string;
  pagadorInscricao?: string;
  codigoConvenio?: string;
  momentoNotificacao?: string;
  idDevolucao?: string;
}

@Component({
  selector: "app-recebimentos-report",
  templateUrl: "./recebimentos-report.component.html",
  styleUrls: ["./recebimentos-report.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class RecebimentosReportComponent implements OnInit, OnDestroy {
  env;
  reportData: RecebimentosReportData;
  private isGeneratingReport = false;
  private generateReportTimeout: any;
  private pixCache: Map<string, PixNaoPago[]> = new Map();
  private lastPixCall = "";

  @Input() payments: Payment[] = [];
  @Input() periodo: { inicio: Date; fim: Date };
  @Input() printReport: boolean = false;
  @Output() printReportChange = new EventEmitter<boolean>();

  constructor(private chargesStore: ChargesStoreService) {
    this.env = environment;
  }

  ngOnInit() {
    this.generateReportData();
  }

  ngOnDestroy() {
    this.pixCache.clear();
    this.lastPixCall = "";
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.printReport && changes.printReport.currentValue) {
      this.htmlToPdf();
      this.printReport = false;
      this.printReportChange.emit(this.printReport);
    }

    const hasPaymentsChanged = changes.payments && changes.payments.currentValue !== changes.payments.previousValue;
    const hasPeriodoChanged = changes.periodo && changes.periodo.currentValue !== changes.periodo.previousValue;

    if (hasPaymentsChanged || hasPeriodoChanged) {
      if (hasPeriodoChanged) {
        this.pixCache.clear();
        this.lastPixCall = "";
      }
    }
  }

  private formatCpf(cpf: string): string {
    if (!cpf || cpf.length !== 11) return cpf;
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }

  private async generateReportData() {
    if (!this.payments || !this.periodo) {
      return;
    }

    if (this.isGeneratingReport) {
      return;
    }

    this.isGeneratingReport = true;

    try {
      const cobradores = this.groupPaymentsByCollector();
      const totalGeral = this.calculateTotalGeral(cobradores);

      const pixNaoPagos = await this.getPixNaoPagos();

      this.reportData = {
        periodo: {
          inicio: this.formatDateForDisplay(this.periodo.inicio),
          fim: this.formatDateForDisplay(this.periodo.fim),
        },
        cobradores,
        totalGeral,
        pixNaoPagos,
      };
    } catch (error) {
      console.error("Erro ao gerar dados do relatório:", error);
    } finally {
      this.isGeneratingReport = false;
    }
  }

  private groupPaymentsByCollector(): CobradorRecebimentos[] {
    const cobradorMap = new Map<string, CobradorRecebimentos>();

    this.payments.forEach(payment => {
      const cpf = this.formatCpf(payment.cobrador);
      const cobradorNome = this.chargesStore.getCollectorName(payment.cobrador);

      if (!cobradorMap.has(payment.cobrador)) {
        cobradorMap.set(payment.cobrador, {
          nome: cobradorNome,
          cpf,
          recebimentos: [],
          totalizador: [],
        });
      }

      const cobrador = cobradorMap.get(payment.cobrador)!;

      const recebimento: RecebimentoItem = {
        paymentId: payment._id || "",
        contrato: payment.contrato,
        dataVencimento: this.getVencimentoFromPayment(payment),
        dataPagamento: this.formatDateOnly(new Date(payment.dt_cobranca)),
        nomeCliente: payment.cliente,
        valorPago: payment.total,
        formasPagamento: this.getFormasPagamentoFromPayment(payment),
        cnpj: payment.cnpj,
      };

      cobrador.recebimentos.push(recebimento);
    });

    cobradorMap.forEach(cobrador => {
      cobrador.totalizador = this.calculateCobradorTotalizador(cobrador.recebimentos);
    });

    return Array.from(cobradorMap.values());
  }

  private getFormasPagamentoFromPayment(payment: Payment): string[] {
    const formas = new Set<string>();

    if (payment.baixas && payment.baixas.length > 0) {
      payment.baixas.forEach(baixa => {
        if (baixa.forma_pagamento) {
          const formaPagamento = this.chargesStore.getPaymentType(baixa.forma_pagamento);
          if (formaPagamento) {
            formas.add(formaPagamento);
          }
        }
      });
    }

    if (formas.size === 0 && payment.forma_pagamento) {
      const formaPagamento = this.chargesStore.getPaymentType(payment.forma_pagamento);
      if (formaPagamento) {
        formas.add(formaPagamento);
      }
    }

    if (formas.size === 0) {
      formas.add("Não especificado");
    }

    return Array.from(formas);
  }

  private getVencimentoFromPayment(payment: Payment): string {
    if (payment.baixas && payment.baixas.length > 0) {
      for (let i = 0; i < payment.baixas.length; i++) {
        const baixa = payment.baixas[i];

        if (baixa.vencimento) {
          const parsed = this.parseDateFromYYYYMMDD(baixa.vencimento);
          if (parsed) {
            return this.formatDateOnly(parsed);
          }

          const fallbackDate = new Date(baixa.vencimento);
          if (!isNaN(fallbackDate.getTime())) {
            return this.formatDateOnly(fallbackDate);
          }
        }
      }
    }

    return "";
  }
  private parseDateFromYYYYMMDD(value: string): Date | null {
    if (!value || value.length !== 8) return null;
    const year = Number(value.substring(0, 4));
    const month = Number(value.substring(4, 6));
    const day = Number(value.substring(6, 8));
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  private calculateCobradorTotalizador(recebimentos: RecebimentoItem[]): TotalizadorPagamento[] {
    const totalizadorMap = new Map<string, number>();

    recebimentos.forEach(recebimento => {
      const payment = this.payments.find(p => p._id === recebimento.paymentId);
      const distribution = this.calculatePaymentDistribution(payment, recebimento);

      distribution.forEach(({ formaPagamento, valor }) => {
        totalizadorMap.set(formaPagamento, (totalizadorMap.get(formaPagamento) || 0) + valor);
      });
    });

    return Array.from(totalizadorMap.entries()).map(([formaPagamento, valor]) => ({
      formaPagamento,
      valor,
    }));
  }

  private calculatePaymentDistribution(
    payment: Payment | undefined,
    recebimento: RecebimentoItem
  ): TotalizadorPagamento[] {
    const paymentTotal = Number(recebimento.valorPago) || 0;

    if (!payment) {
      return this.distributeFallback(recebimento.formasPagamento, paymentTotal);
    }

    const baixaDistribution = this.getBaixaDistribution(payment);
    const totalBase = baixaDistribution.reduce((sum, item) => sum + item.valorBase, 0);

    if (baixaDistribution.length > 0 && totalBase > 0) {
      let allocated = 0;

      return baixaDistribution.map((item, index) => {
        const isLastItem = index === baixaDistribution.length - 1;
        const valor = isLastItem
          ? paymentTotal - allocated
          : this.roundCurrency((paymentTotal * item.valorBase) / totalBase);

        allocated += valor;

        return {
          formaPagamento: item.formaPagamento,
          valor,
        };
      });
    }

    return this.distributeFallback(this.getFormasPagamentoFromPayment(payment), paymentTotal);
  }

  private getBaixaDistribution(payment: Payment): BaixaDistribution[] {
    if (!payment.baixas || payment.baixas.length === 0) {
      return [];
    }

    const totalsByType = new Map<string, number>();

    payment.baixas.forEach(baixa => {
      const formaPagamento = this.chargesStore.getPaymentType(baixa.forma_pagamento || payment.forma_pagamento);
      if (!formaPagamento) {
        return;
      }

      const valorBase =
        Number(baixa.valor_titulo || 0) + Number(baixa.valor_juros || 0) + Number(baixa.valor_multa || 0);

      if (valorBase <= 0) {
        return;
      }

      totalsByType.set(formaPagamento, (totalsByType.get(formaPagamento) || 0) + valorBase);
    });

    return Array.from(totalsByType.entries()).map(([formaPagamento, valorBase]) => ({
      formaPagamento,
      valorBase,
    }));
  }

  private distributeFallback(formasPagamento: string[], total: number): TotalizadorPagamento[] {
    if (!formasPagamento || formasPagamento.length === 0) {
      return [];
    }

    const totalizadores: TotalizadorPagamento[] = [];
    let allocated = 0;

    formasPagamento.forEach((formaPagamento, index) => {
      const isLastItem = index === formasPagamento.length - 1;
      const valor = isLastItem ? total - allocated : this.roundCurrency(total / formasPagamento.length);

      allocated += valor;
      totalizadores.push({ formaPagamento, valor });
    });

    return totalizadores;
  }

  private roundCurrency(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
  private calculateTotalGeral(cobradores: CobradorRecebimentos[]): TotalizadorPagamento[] {
    const totalMap = new Map<string, number>();

    cobradores.forEach(cobrador => {
      cobrador.totalizador.forEach(item => {
        totalMap.set(item.formaPagamento, (totalMap.get(item.formaPagamento) || 0) + item.valor);
      });
    });

    const totals = Array.from(totalMap.entries()).map(([formaPagamento, valor]) => ({
      formaPagamento,
      valor,
    }));

    const totalGeralValor = totals.reduce((sum, item) => sum + item.valor, 0);
    totals.push({ formaPagamento: "Total Geral", valor: totalGeralValor });

    return totals;
  }

  private async getPixNaoPagos(): Promise<PixNaoPago[]> {
    try {
      const dtInicial = this.formatDateForApi(this.periodo.inicio);
      const dtFinal = this.formatDateForApi(this.periodo.fim);

      const cacheKey = `${this.env.selected.cnpj}-${dtInicial}-${dtFinal}`;

      if (this.lastPixCall === cacheKey) {
        console.log("Evitando chamada duplicada de PIX");
        return this.pixCache.get(cacheKey) || [];
      }

      if (this.pixCache.has(cacheKey)) {
        console.log("Usando PIX do cache");
        return this.pixCache.get(cacheKey) || [];
      }

      this.lastPixCall = cacheKey;

      const response = await this.chargesStore
        .getPixStatus(
          this.env.selected.cnpj,
          "",
          "",
          "REGISTRADO",
          dtInicial,
          dtFinal,
          undefined,
          this.chargesStore.paymentFilter.filial,
          this.chargesStore.paymentFilter.cobrador
        )
        .toPromise();

      const pixData = this.mapPixResponseToReport(response || []);
      this.pixCache.set(cacheKey, pixData);

      return pixData;
    } catch (error) {
      console.error("Erro ao buscar PIX não pagos:", error);
      return [];
    }
  }

  private formatDateForApi(date: Date): string {
    if (!date) return "";
    return date.toISOString().split("T")[0]; // formato YYYY-MM-DD
  }

  private mapPixResponseToReport(response: PixTransaction[]): PixNaoPago[] {
    if (!response || !Array.isArray(response)) {
      return [];
    }

    return response.map(item => ({
      contrato: item.contrato || "",
      dataGeracao: this.formatDateForDisplay(new Date(item.data_solicitacao_origem)),
      nomeCliente: item.cgc_cliente || "Cliente não identificado",
      valor: parseFloat(item.valor.toString()) || 0,
      status: this.mapPixStatus(item.situacao) || "Aguardando P.",
    }));
  }

  private mapPixStatus(situacao: string): string {
    // Mapear os status do TotalBankStatus para textos legíveis
    switch (situacao) {
      case "REGISTRADO":
        return "Aguardando P.";
      case "LIQUIDADO":
        return "Pago";
      case "CANCELADO":
        return "Cancelado";
      case "EXPIRADO":
        return "EXPIRADO";
      default:
        return situacao || "CANCELADO";
    }
  }

  private formatDateForDisplay(date: Date): string {
    if (!date) return "";
    return date.toLocaleString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  private formatDateOnly(date: Date): string {
    if (!date) return "";
    return date.toLocaleDateString("pt-BR");
  }
  htmlToPdf() {
    const content = document.getElementById("contentToPrint");
    if (!content) {
      return;
    }

    const backTitle = document.title;
    const filename = `Relatorio-${this.formatDateForFilename(new Date())}`;
    document.title = filename;

    const printWindow = window.open("", "_blank", "width=1024,height=768");
    if (!printWindow) {
      document.title = backTitle;
      return;
    }

    const styles = `
      <style>
        @page { size: A4; margin: 10mm; }
        html, body { width: 100%; height: auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; color: #333; }
        table { width: 100%; border-collapse: collapse; table-layout: auto; }
        th, td { border: 1px solid #e0e0e0; padding: 4px 6px; font-size: 10px; vertical-align: top; word-break: break-word; overflow-wrap: anywhere; line-height: 1.25; }
        th { background: #004080; color: #fff; font-weight: bold; }
        tr { break-inside: avoid; page-break-inside: avoid; }
        thead { display: table-header-group; }
        .header h1 { font-size: 20px; margin: 0 0 6px; color: #004080; }
        .header p { margin: 0 0 12px; font-size: 12px; }
        .cobrador-title, .section-title { color: #004080; font-size: 14px; font-weight: bold; margin: 12px 0 8px; }
        .report-table tbody tr:nth-child(even) { background: #f0f4f8; }
        .totalizador-table th { text-align: center; }
        .total-geral-label { font-weight: bold; }
        .pix-note { font-style: italic; color: #666; margin-top: 8px; font-size: 11px; }
        .page-break { page-break-after: always; break-after: page; height: 0; }
      </style>
    `;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>${filename}</title>
          ${styles}
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.focus();
    printWindow.print();
    printWindow.onafterprint = () => {
      printWindow.close();
      document.title = backTitle;
    };
  }

  private formatDateForFilename(date: Date): string {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }
}
